'use client'

import { useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table'
import type {
  ColumnDef,
  PaginationState,
  SortingState,
  OnChangeFn,
  RowData,
} from '@tanstack/react-table'
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  Table,
  IconButton,
  Spinner,
} from '@chakra-ui/react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import { glassPanel } from '@/shared/lib/chakra/glass'

// ─── Pagination helpers ───────────────────────────────────────────────────────

/** Pages kept on each side of the current one before collapsing into an ellipsis. */
const PAGE_SIBLINGS = 1

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i)

/**
 * Builds the page list for the pager, e.g. `[1, 'ellipsis', 4, 5, 6, 'ellipsis', 20]`.
 * `current` is 1-based. The window slides with the current page, first/last are always shown.
 */
export function getPageItems(
  current: number,
  pageCount: number,
): (number | 'ellipsis')[] {
  // TanStack reports -1 while a manual-pagination row count is still unknown
  if (pageCount < 1) return [current]

  // first + last + both ellipses + current + siblings on each side
  const maxSlots = PAGE_SIBLINGS * 2 + 5
  if (pageCount <= maxSlots) return range(1, pageCount)

  const left = Math.max(current - PAGE_SIBLINGS, 1)
  const right = Math.min(current + PAGE_SIBLINGS, pageCount)
  const showLeftEllipsis = left > 2
  const showRightEllipsis = right < pageCount - 1
  const edgeBlock = PAGE_SIBLINGS * 2 + 3

  if (!showLeftEllipsis && showRightEllipsis)
    return [...range(1, edgeBlock), 'ellipsis', pageCount]

  if (showLeftEllipsis && !showRightEllipsis)
    return [1, 'ellipsis', ...range(pageCount - edgeBlock + 1, pageCount)]

  return [1, 'ellipsis', ...range(left, right), 'ellipsis', pageCount]
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DataTableProps<TData extends RowData> {
  data: TData[]
  columns: ColumnDef<TData>[]
  onRowClick?: (row: TData) => void

  // Per-row background override — e.g. warning colour on rows near expiry.
  // Falls back to the zebra stripe when it returns nothing.
  rowBg?: (row: TData) => string | undefined

  // Controlled global filter (pass both or neither)
  globalFilter?: string
  onGlobalFilterChange?: OnChangeFn<string>

  // Controlled pagination (pass both or neither; otherwise internal state is used)
  pagination?: PaginationState
  onPaginationChange?: OnChangeFn<PaginationState>

  // Set true + rowCount for server-side pagination/filtering
  manualPagination?: boolean
  rowCount?: number

  // Pagination row label — receives (start, end, total) and returns a string
  renderPaginationLabel?: (start: number, end: number, total: number) => string

  // Default page size when running in uncontrolled mode
  defaultPageSize?: number

  // UI states
  isLoading?: boolean
  emptyLabel?: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DataTable<TData extends RowData>({
  data,
  columns,
  globalFilter: externalFilter,
  onGlobalFilterChange,
  pagination: externalPagination,
  onPaginationChange,
  manualPagination = false,
  rowCount,
  renderPaginationLabel,
  defaultPageSize = 10,
  isLoading = false,
  emptyLabel = '—',
  onRowClick,
  rowBg,
}: DataTableProps<TData>) {
  // Internal (uncontrolled) state — used when the parent doesn't pass controlled props
  const [internalFilter, setInternalFilter] = useState('')
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize: defaultPageSize,
    },
  )
  const [sorting, setSorting] = useState<SortingState>([])

  const isFilterControlled = externalFilter !== undefined
  const isPaginationControlled = externalPagination !== undefined

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter: isFilterControlled ? externalFilter : internalFilter,
      pagination: isPaginationControlled
        ? externalPagination
        : internalPagination,
      sorting,
    },
    onGlobalFilterChange: isFilterControlled
      ? onGlobalFilterChange
      : setInternalFilter,
    onPaginationChange: isPaginationControlled
      ? onPaginationChange
      : setInternalPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(!manualPagination && { getFilteredRowModel: getFilteredRowModel() }),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination,
    rowCount: manualPagination ? rowCount : undefined,
  })

  const { pageIndex, pageSize } = table.getState().pagination
  const total = manualPagination
    ? (rowCount ?? 0)
    : table.getFilteredRowModel().rows.length
  const start = total === 0 ? 0 : pageIndex * pageSize + 1
  const end = Math.min((pageIndex + 1) * pageSize, total)

  const visibleColumnCount = table.getVisibleLeafColumns().length
  const pageCount = table.getPageCount()
  const pageItems = getPageItems(pageIndex + 1, pageCount)

  return (
    /* Floating card: no outline, elevation only — matches AppCard. */
    <Box {...glassPanel} borderRadius="xl" overflow={'hidden'}>
      <Box overflow="hidden" overflowX={'scroll'}>
        {/* Rows stay transparent so the panel fill (glass in dark mode) shows through. */}
        <Table.Root size="sm" bg="transparent">
          {/* Header */}
          <Table.Header bg="transparent">
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id} bg="transparent">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sorted = header.column.getIsSorted()
                  return (
                    <Table.ColumnHeader
                      key={header.id}
                      py={3}
                      px={4}
                      fontSize="xs"
                      color="fg.muted"
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      borderColor="border.muted"
                      cursor={canSort ? 'pointer' : 'default'}
                      userSelect={canSort ? 'none' : undefined}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      _hover={canSort ? { color: 'fg' } : {}}
                      transition="color 0.15s"
                    >
                      <HStack gap={1} display="inline-flex">
                        <span>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </span>
                        {canSort && (
                          <Box
                            color={sorted ? 'primary.fg' : 'fg.subtle'}
                            flexShrink={0}
                          >
                            {sorted === 'asc' ? (
                              <ChevronUp size={13} />
                            ) : sorted === 'desc' ? (
                              <ChevronDown size={13} />
                            ) : (
                              <ChevronsUpDown size={13} />
                            )}
                          </Box>
                        )}
                      </HStack>
                    </Table.ColumnHeader>
                  )
                })}
              </Table.Row>
            ))}
          </Table.Header>

          {/* Body */}
          <Table.Body bg="transparent">
            {isLoading ? (
              <Table.Row bg="transparent">
                <Table.Cell
                  colSpan={visibleColumnCount}
                  py={10}
                  textAlign="center"
                  borderBottomWidth="0px"
                >
                  <Flex justify="center">
                    <Spinner size="sm" color="primary.fg" />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : table.getRowModel().rows.length === 0 ? (
              <Table.Row>
                <Table.Cell
                  colSpan={visibleColumnCount}
                  py={10}
                  textAlign="center"
                  color="fg.muted"
                  fontSize="sm"
                  borderBottomWidth="0px"
                >
                  {emptyLabel}
                </Table.Cell>
              </Table.Row>
            ) : (
              table.getRowModel().rows.map((row, index) => (
                <Table.Row
                  key={row.id}
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                  cursor={onRowClick ? 'pointer' : undefined}
                  _hover={{ bg: 'bg.subtle' }}
                  transition="background 0.1s"
                  bg={
                    rowBg?.(row.original) ??
                    (index % 2 === 0 ? 'transparent' : 'bg.stripe')
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    /* Rows are told apart by the zebra stripe, not by rules. */
                    <Table.Cell
                      key={cell.id}
                      py={0}
                      px={0}
                      borderBottomWidth="0px"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>

        {/* Pagination */}
      </Box>
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={3}
        borderTopWidth="1px"
        borderColor="border.muted"
      >
        <Text fontSize="sm" color="fg.muted">
          {renderPaginationLabel
            ? renderPaginationLabel(start, end, total)
            : `${start}–${end} / ${total}`}
        </Text>

        <HStack gap={1}>
          <IconButton
            variant="ghost"
            size="xs"
            aria-label="Previous page"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
            color="fg.muted"
          >
            <ChevronLeft size={16} />
          </IconButton>
          {pageItems.map((item, i) =>
            item === 'ellipsis' ? (
              <Box
                key={`ellipsis-${i}`}
                px={1}
                fontSize="sm"
                color="fg.subtle"
                userSelect="none"
              >
                …
              </Box>
            ) : (
              <Button
                key={item}
                size="xs"
                minW="28px"
                px={2}
                borderRadius="sm"
                fontSize="sm"
                variant={item === pageIndex + 1 ? 'solid' : 'ghost'}
                bg={item === pageIndex + 1 ? 'primary.solid' : undefined}
                color={item === pageIndex + 1 ? 'white' : 'fg.muted'}
                _hover={
                  item === pageIndex + 1
                    ? { bg: 'primary.solid' }
                    : { bg: 'bg.subtle', color: 'fg' }
                }
                aria-label={`Page ${item}`}
                aria-current={item === pageIndex + 1 ? 'page' : undefined}
                onClick={() => table.setPageIndex(item - 1)}
              >
                {item}
              </Button>
            ),
          )}
          <IconButton
            variant="ghost"
            size="xs"
            aria-label="Next page"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
            color="fg.muted"
          >
            <ChevronRight size={16} />
          </IconButton>
        </HStack>
      </Flex>
    </Box>
  )
}
